import React, { useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import { CustomTextField } from "assets/styles/globalStyledComponent";
import { LoadingButton } from "@mui/lab";
import CustomLoader from "app/components/common/CustomLoader";
import {
  getVLwaConfigBySchool,
  upsertVLwaConfig,
} from "app/services/schoolReportingServices/communicationSetting.service";

const Configuration = () => {
  const [state, setState] = useState({
    waba_id: "",
    phone_number_id: "",
    apiPassword: "",
    isChanged: false,
  });

  const [initialData, setInitialData] = useState({
    waba_id: "",
    phone_number_id: "",
    apiPassword: "",
  });

  // Fetch configuration data
  const {
    isLoading: configLoading,
    isFetching: configFetching,
    refetch: refetchConfig,
  } = useQuery({
    queryKey: ["getVLwaConfigBySchool"],
    queryFn: () => getVLwaConfigBySchool(),
    onSuccess: (data) => {
      const config = data?.data?.obj || {};
      const newData = {
        waba_id: config?.waba_id || "",
        phone_number_id: config?.phone_number_id || "",
        apiPassword: config?.apiPassword || "",
      };
      setState((prev) => ({
        ...newData,
        isChanged: false,
      }));
      setInitialData(newData);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to load configuration"
      );
    },
  });

  // Update configuration mutation
  const { mutate: updateConfig, isLoading: updateLoading } = useMutation(
    upsertVLwaConfig,
    {
      onSuccess: (data) => {
        toast.success(
          data?.data?.message || "Configuration saved successfully"
        );
        refetchConfig();
        setState((prev) => ({ ...prev, isChanged: false }));
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to save configuration"
        );
      },
    }
  );

  // Handle input change
  const handleChange = (field, value) => {
    setState((prev) => {
      const newState = { ...prev, [field]: value };
      // Check if any value has changed from initial data
      const hasChanged =
        newState.waba_id !== initialData.waba_id ||
        newState.phone_number_id !== initialData.phone_number_id ||
        newState.apiPassword !== initialData.apiPassword;
      return { ...newState, isChanged: hasChanged };
    });
  };

  // Handle save
  const handleSave = () => {
    const payload = {
      waba_id: state.waba_id,
      phone_number_id: state.phone_number_id,
      apiPassword: state.apiPassword,
    };
    updateConfig(payload);
  };

  // Handle clear/reset
  const handleClear = () => {
    setState({
      ...initialData,
      isChanged: false,
    });
  };

  return (
    <>
      <Helmet>
        <title>WhatsApp Configuration</title>
      </Helmet>

      <CustomLoader show={configLoading || configFetching} />

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextFieldLabel title="WABA ID" />
            <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter WABA ID"
              value={state.waba_id}
              onChange={(e) => handleChange("waba_id", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextFieldLabel title="Phone Number ID" />
            <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter Phone Number ID"
              value={state.phone_number_id}
              onChange={(e) => handleChange("phone_number_id", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldLabel title="API Password" />
            <CustomTextField
              fullWidth
              size="small"
              type="password"
              placeholder="Enter API Password"
              value={state.apiPassword}
              onChange={(e) => handleChange("apiPassword", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleClear}
                disabled={!state.isChanged || updateLoading}
              >
                Clear
              </Button>
              <LoadingButton
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleSave}
                disabled={!state.isChanged}
                loading={updateLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Configuration;
